import AddBoxIcon from "@mui/icons-material/AddBox";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/DeleteOutline";

import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { styled, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { ConfirmResponse, useConfirmDialog } from "app/providers/confirm-dialog";
import { useDialog } from "app/providers/dialog";
import { PageTab } from "app/routes/fee-schedules/tabs";
import { CanceledError } from "axios";
import { OrderDirection } from "common/contracts/order-direction";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";

import { NotAuthorized } from "common/components/not-authorized";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  MaterialReactTable,
  type MRT_PaginationState as PaginationState,
  type MRT_Row as Row,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import { useSession } from "users/session/session-context";
import FeeScheduleTemplateAPIService from "work/entities/fee-schedule-template/api/fee-schedule-template-api-service";
import FeeScheduleTemplateQuery, { FeeScheduleTemplateFilterParameters, FeeScheduleTemplateOrderField, FeeScheduleTemplateOrderParameters } from "work/entities/fee-schedule-template/api/request-contracts/query-fee-schedule-template-api-request";
import FeeScheduleTemplate from "work/entities/fee-schedule-template/fee-schedule-template";
import CreateTemplateFeeScheduleDialog from "work/entities/fee-schedule-template/view/create-template-fee-schedule-dialog";

const ToolbarButtonsContainer = styled("div")(
  ({ theme }) => ({
    [theme.breakpoints.down('md')]: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
    },
    display: "flex",
    alignItems: "end",
    flexDirection: "column",
    gap: theme.spacing(1),
  }));
const ToolbarButtons = styled("div")(
  () => ({
    display: "flex",
    alignItems: "end",
    flexWrap: "nowrap"
  }));
const TagChip = styled(Chip)(
  ({ theme }) => ({
    color: '#777',
    marginRight: theme.spacing(0.4),
  }));
const TableTopToolbarContainer = styled("div")(
  () => ({
    display: "flex",
    flexDirection: "column",
    width: "100%",
  }));
const LowerTopToolbarContainer = styled("div")(
  ({ theme }) => ({
    [theme.breakpoints.down('md')]: {
      flexDirection: "column",
    },
    display: "flex",
    flexDirection: "row",
    alignItems: "left",
    width: "100%",
  }));
const TableTitle = styled(Typography)(
  ({ theme }) => ({
    paddingLeft: theme.spacing(1),
  }));
const RowTitle = styled(Typography)(
  () => ({
    fontSize: "1.1em",
    fontWeight: "600",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }));
const RowActionsContainer = styled(Box)(
  () => ({
    display: 'flex',
    flexWrap: 'nowrap',
    width: "inherit"
  }));
const NewButton = styled(IconButton)(
  () => ({
    padding: 0
  }));

const NameCell = ({ row }: { row: Row<FeeScheduleTemplate> }) => (
  <RowTitle>{row.original.name}</RowTitle>
);

export default function FeeSchedules() {
  const isDirtyRef = React.useRef<boolean>(false);

  const [isLoading, setIsLoading] = React.useState(false);
  const [isRefetching, setIsRefetching] = React.useState(false);
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [feeScheduleTemplates, setFeeScheduleTemplates] = React.useState<FeeScheduleTemplate[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [wasErrorLoading, setWasErrorLoading] = React.useState(false);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [activeTab, setActiveTab] = React.useState<PageTab>(PageTab.Templates);

  const routeParams = useLoaderData();
  const navigate = useNavigate();
  const session = useSession();
  const { openDialog, closeDialog } = useDialog();
  const confirm = useConfirmDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  // Handle URL changes
  useEffect(
    () => {
      if (!session.user?.companyEntityId || !session.user?.isCompanyManager) return;

      if (routeParams.tab) {
        setActiveTab(routeParams.tab as PageTab);
      } else {
        navigate(`/fee-schedules/${PageTab.Templates}`);
      }

      if (routeParams.action === 'create') {
        beginCreateFeeScheduleTemplate();
        return;
      }

      if (routeParams.tab === PageTab.Templates && routeParams.id) {
        handleEditFeeScheduleTemplate(new Guid(routeParams.id));
      }

      void fetchData(routeParams.tab as PageTab);
    },
    [routeParams]
  );

  useEffect(
    () => {
      if (!session.user?.companyEntityId || !session.user?.isCompanyManager) return;

      let abortController = new AbortController();
      void fetchData(activeTab, abortController);

      return () => {
        abortController.abort();
        abortController = new AbortController();
      };
    },
    [
      columnFilters,
      globalFilter,
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
    ]
  );

  const fetchData = async (_tab?: PageTab, abortController?: AbortController) => {
    if (!feeScheduleTemplates.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    try {
      setIsLoading(true);

      let feeSchedules: FeeScheduleTemplate[] = [];

      if (session.hasEntity) {
        feeSchedules = await getFeeScheduleTemplates(
          session.entities[0].entityId,
          abortController ?? new AbortController()
        );
      }

      setWasErrorLoading(false);
      setIsLoading(false);
      setIsRefetching(false);
      setFeeScheduleTemplates(feeSchedules);
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
      setWasErrorLoading(true);
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  async function getFeeScheduleTemplates(entityId: Guid, abortController: AbortController)
    : Promise<FeeScheduleTemplate[]> {
    const getArchived = activeTab === PageTab.Archived;

    const filterParams = new FeeScheduleTemplateFilterParameters(
      entityId,
      globalFilter,
      getArchived,
      {
        name: columnFilters.find((filter) => filter.id === "name")?.value as string,
        tags: columnFilters.find((filter) => filter.id === "tags")?.value as string,
        createdDate: columnFilters.find((filter) => filter.id === "createdDate")?.value as string,
      }
    );

    const orderParameters = new FeeScheduleTemplateOrderParameters(
      sorting[0]?.id as FeeScheduleTemplateOrderField ?? FeeScheduleTemplateOrderField.Name,
      sorting[0]?.desc === false ? OrderDirection.Ascending : OrderDirection.Descending
    );

    const paginatedParameters = new PaginationParameters(
      pagination.pageIndex,
      pagination.pageSize
    );

    const templateQuery: FeeScheduleTemplateQuery = new FeeScheduleTemplateQuery(
      filterParams,
      orderParameters,
      paginatedParameters
    );
    const feeScheduleService = new FeeScheduleTemplateAPIService(session);
    const templates =
      await feeScheduleService.getFeeScheduleTemplates(
        templateQuery,
        abortController
      );

    setRowCount(templates.totalElements);
    return templates.data;
  }

  function beginCreateFeeScheduleTemplate() {
    const parentPath = new URL(
      ".",
      globalThis.location.origin + globalThis.location.pathname
    );

    openDialog(
      {
        title: 'Create Template Fee Schedule',
        MuiProps: { maxWidth: 'xl' },
        isDirtyRef: isDirtyRef,
        disableSave: true,
        component: (
          <CreateTemplateFeeScheduleDialog
            entityId={session.entities[0]?.entityId}
            onDirty={(dirty) => {
              isDirtyRef.current = dirty;
            }}
            onSave={() => {
              isDirtyRef.current = false;
              closeDialog();
              navigate(parentPath.pathname);
              void fetchData();
            }}
          />
        ),
        onClose: () => {
          isDirtyRef.current = false;
          closeDialog();
        }

      },
      () => navigate(parentPath.pathname),
    );
  }

  function handleEditFeeScheduleTemplate(id: Guid) {
    const template = feeScheduleTemplates.find(template => template.id?.isEqualTo(id));

    if (!template) {
      enqueueSnackbar(
        'Fee schedule template not found',
        { variant: 'error' }
      );
      return;
    }

    const parentPath = new URL(
      ".",
      globalThis.location.origin + globalThis.location.pathname
    );

    openDialog(
      {
        title: 'Edit Template Fee Schedule',
        MuiProps: { fullWidth: isMediumDisplaySize, maxWidth: 'xl' },
        isDirty: isDirtyRef.current,
        disableSave: true,
        component: (
          <CreateTemplateFeeScheduleDialog
            entityId={session.entities[0].entityId}
            feeSchedule={template}
            onDirty={(dirty) => {
              isDirtyRef.current = dirty;
            }}
            onSave={() => {
              isDirtyRef.current = false;
              closeDialog();
              navigate(parentPath.pathname);
              void fetchData();
            }}
          />
        ),
        onClose: () => {
          isDirtyRef.current = false;
          closeDialog();
        }
      },
      () => navigate(parentPath.pathname)
    );
  }

  /**
   * Archive or Unarchive the specified fee schedule template
   * @param id fee schedule id to archive / unarchive
   * @param archive if true, archive, else unarchive
   */
  async function handleArchiveFeeScheduleTemplate(id: Guid | undefined, archive: boolean): Promise<void> {
    if (!id) {
      await Promise.reject(new Error("No fee schedule template id provided"));
      return;
    }

    const action = archive ? "Archive" : "Unarchive";

    try {
      const response = await confirm({
        title: action,
        message: `${action} fee schedule template?`,
        okButtonText: action,
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      const feeScheduleService = new FeeScheduleTemplateAPIService(session);

      if (archive)
        await feeScheduleService.archiveFeeScheduleTemplate(id);
      else
        await feeScheduleService.unarchiveFeeScheduleTemplate(id);
      enqueueSnackbar(
        `${action} fee schedule`,
        { variant: "success" }
      );
      await fetchData();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        `Failed to ${action} fee schedule. Please try again`,
        {
          variant: "error",
        }
      );
    }
  }

  /**
   * Deletes the specified fee schedule template
   * @param id The fee schedule template id
   */
  async function handleDeleteFeeScheduleTemplate(id: Guid | undefined): Promise<void> {
    if (!id) {
      await Promise.reject(new Error("No fee schedule template id provided"));
      return;
    }

    try {
      const response = await confirm({
        title: "Delete?",
        message: `Delete fee schedule template?`,
        okButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel)
        return;

      const feeScheduleService = new FeeScheduleTemplateAPIService(session);
      await feeScheduleService.deleteFeeScheduleTemplate(id);
      enqueueSnackbar(
        "Deleted fee schedule",
        { variant: "success" }
      );
      await fetchData();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to delete fee schedule. Please try again",
        {
          variant: "error",
        }
      );
    }
  }

  function renderTagsColumn(template: FeeScheduleTemplate) {
    if (!template.tags.length) return <Icon>remove</Icon>;
    return (
      <div>
        {template.tags.map((tag) => (
          <TagChip key={tag.value as React.Key} label={tag.value} size="small" />
        ))}
      </div>
    )
  }

  function renderCreatedDateColumn(template: FeeScheduleTemplate) {
    return (
      <time>{template.createdDate?.format('MM/DD/YYYY hh:mm A')}</time>
    )
  }

  const columnHelper = createMRTColumnHelper<FeeScheduleTemplate>();

  const columns = [
    columnHelper.accessor(
      (feeScheduleTemplate) => feeScheduleTemplate.name,
      {
        header: "Name",
        id: "name",
        Cell: NameCell,
      }
    ),
    columnHelper.accessor(
      renderTagsColumn,
      {
        header: "Tags",
        id: "tags",
        enableSorting: false,
      }
    ),
    columnHelper.accessor(
      renderCreatedDateColumn,
      {
        header: "Created",
        id: "createdDate",
      }
    ),
  ];

  const table = useMaterialReactTable({
    columns,
    data: feeScheduleTemplates,
    enableRowSelection: false,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        if (!row.original.id) return;
        handleEditFeeScheduleTemplate(row.original.id);
      },
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <Button
              startIcon={<AddBoxIcon />}
              onClick={() => navigate(`/fee-schedules/${PageTab.Templates}/create`)}>
              Create Fee Schedule
            </Button>
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton
            color="primary"
            onClick={() => navigate(`/fee-schedules/${PageTab.Templates}/create`)}
          >
            <AddBoxIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    renderTopToolbarCustomActions: () => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">
            Fee Schedules
          </TableTitle>
          <LowerTopToolbarContainer>
            <Tabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => navigate(`/fee-schedules/${newValue}/`)}
            >
              <Tab value={PageTab.Templates} label='Templates' />
              <Tab value={PageTab.Active} label='Active' />
              <Tab value={PageTab.Archived} label="Archived" />
            </Tabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    getRowId: (row) => row.id?.toString() ?? Guid.generate().toString(),
    enableRowActions: activeTab !== PageTab.Active,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ['mrt-row-actions'] }
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    renderRowActions: ({ row }) => {
      return (
        <RowActionsContainer>
          {activeTab !== PageTab.Active && (
            <Tooltip title={!row.original.archivedDate ? "Archive Template" : "Un-Archive Template"}>
              <span>
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!row.original.id) return;
                    void handleArchiveFeeScheduleTemplate(
                      row.original.id,
                      !row.original.archivedDate
                    );
                  }}
                >
                  {!row.original.archivedDate ? <ArchiveIcon /> : <UnarchiveIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {activeTab === PageTab.Templates && (
            <Tooltip title="Delete Template">
              <span>
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!row.original.id) return;
                    void handleDeleteFeeScheduleTemplate(row.original.id);
                  }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </RowActionsContainer>
      );
    },
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: wasErrorLoading
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: wasErrorLoading,
      showProgressBars: isRefetching,
      sorting,
    },
  });

  if (!session.user?.companyEntityId || !session.user?.isCompanyManager) {
    return <NotAuthorized />;
  }

  return (
    <MaterialReactTable table={table} />
  );
}
